import { apiService } from "./apiService";
import moment from 'moment';
import { getRowsResults, weekDaysDimensions, findRow } from "../utils/ga";

export interface IUploadSearchImageReq {
    searchId: string,
    base64: string,
    provider: string
}

export interface ISearchImageReq {
    key: string,
    searchId: string,
    provider: string,
    email: string
    agencyCode: string
}

export const GoogleAnalyticsService = {
    analytics: () => (window as any).gapi.analytics,
    auth: () => (window as any).gapi.analytics.auth,
    client: () => (window as any).gapi.client,
    realTimeClient: () => (window as any).gapi.client.analytics.data.realtime,
    getGAToken() {
        return apiService.get('api/auth/ga').then(response => {
            return response.data && response.data.token ? response.data.token : '';
        });
    },
    getQuery(body) {
        return this.client().request({
            path: '/v4/reports:batchGet',
            root: 'https://analyticsreporting.googleapis.com/',
            method: 'POST',
            body
        })
    },
    getCountriesInfo(viewId: string, pagePath?: string) {
        return this.getQuery({
            reportRequests: [
                {
                    viewId,
                    dateRanges: [{ startDate: '30daysAgo', endDate: 'today' }],
                    metrics: [{ expression: 'ga:pageviews' }],
                    dimensions: [{ "name": "ga:country" }],
                    ...pagePath ? { filtersExpression: `ga:pagePath==/${pagePath}` } : {}
                }
            ]
        }).then(({ result }) => {
            const { rows = [] } = result.reports[0].data;
            return rows.map((row) => ({
                name: row.dimensions[0],
                count: Number(row.metrics[0].values[0])
            })).filter((row => row.name !== "Costa Rica"))
        })
    },
    getDimensionInfo(viewId: string, dimension: string, pagePath?: string) {
        return this.getQuery({
            reportRequests: [
                {
                    viewId,
                    dateRanges: [{ startDate: '30daysAgo', endDate: 'today' }],
                    metrics: [{ expression: 'ga:pageviews' }],
                    dimensions: [{ "name": dimension }],
                    ...pagePath ? { filtersExpression: `ga:pagePath==/${pagePath}` } : {}
                }
            ]
        }).then(({ result }) => {
            const { rows = [] } = result.reports[0].data
            return rows.map((row) => ({
                name: row.dimensions[0],
                count: Number(row.metrics[0].values[0])
            }))
        })
    },
    getViewsByPage(viewId: string, path: string) {
        return this.getQuery({
            reportRequests: [
                {
                    viewId,
                    metrics: [{ expression: 'ga:pageviews' }],
                    dimensions: [{ "name": "ga:pagePath" }],
                    filtersExpression: `ga:pagePath==/${path}`
                }
            ]
        }).then(({ result }) => {
            const { rows = [] } = result.reports[0].data;
            return rows && rows[0] && rows[0].metrics && rows[0].metrics[0] && rows[0].metrics[0].values[0] ? rows[0].metrics[0].values[0] : 0;
        });
    },
    getViewsInfo(viewId: string, days: number, pagePath?: string) {
        return this.getQuery({
            reportRequests: [
                {
                    viewId,
                    dateRanges: [{ startDate: `${days}daysAgo`, endDate: 'today' }],
                    metrics: [{ expression: 'ga:pageviews' }],
                    dimensions: [{ "name": "ga:date" }],
                    ...pagePath ? { filtersExpression: `ga:pagePath==/${pagePath}` } : {}
                }
            ]
        }).then(({ result }) => {
            const { rows = [] } = result.reports[0].data
            return rows.map((row) => {
                return {
                    date: moment(row.dimensions[0], 'YYYYMMDD').format('MMM DD, YYYY'),
                    count: Number(row.metrics[0].values[0])
                }
            })
        });
    },
    getActiveUsers(viewId: string, pagePath?: string) {
        return this.realTimeClient().get({
            ids: viewId,
            metrics: 'rt:activeUsers',
            ...pagePath ? { filters: `rt:pagePath==/${pagePath}` } : {}
        }).then(({ result: { totalResults = 0 } }) => totalResults);
    },
    getSessionsByWeek(viewId: string, pagePath?: string) {
        const now = moment()
        const lastWeek = this.getQuery({
            reportRequests: [
                {
                    viewId,
                    dateRanges: [{
                        startDate: moment(now).subtract(1, 'day').day(0).subtract(1, 'week').format('YYYY-MM-DD'),
                        endDate: moment(now).subtract(1, 'day').day(6).subtract(1, 'week').format('YYYY-MM-DD')
                    }],
                    metrics: [{ expression: 'ga:sessions' }],
                    dimensions: [{ name: "ga:nthDay" }, { name: "ga:dayOfWeekName" }, { name: "ga:date" }],
                    ...pagePath ? { filtersExpression: `ga:pagePath==/${pagePath}` } : {}
                }
            ]
        });
        const currentWeek = this.getQuery({
            reportRequests: [
                {
                    viewId,
                    dateRanges: [{
                        startDate: moment(now).subtract(1, 'day').day(0).format('YYYY-MM-DD'),
                        endDate: moment(now).format('YYYY-MM-DD')
                    }],
                    metrics: [{ expression: 'ga:sessions' }],
                    dimensions: [{ name: "ga:nthDay" }, { name: "ga:dayOfWeekName" }, { name: "ga:date" }],
                    ...pagePath ? { filtersExpression: `ga:pagePath==/${pagePath}` } : {}
                }
            ]
        });
        return Promise.all([lastWeek, currentWeek]).then(([{ result: lastResult }, { result: currentResult }]) => {
            const lastWeekRows = getRowsResults(lastResult);
            const currentWeekRows = getRowsResults(currentResult);
            const weekDaysList = weekDaysDimensions([...lastWeekRows, ...currentWeekRows]);
            return weekDaysList.reduce((sessionsByWeek, date) => {
                const [key, day] = date;
                const lastWeekRow = findRow(lastWeekRows, key);
                const currentWeekRow = findRow(currentWeekRows, key);
                const record = {
                    day,
                    last: Number(lastWeekRow && lastWeekRow.metrics[0].values[0] || "0"),
                    current: Number(currentWeekRow && currentWeekRow.metrics[0].values[0] || "0"),
                };
                sessionsByWeek = [...sessionsByWeek, record];
                return sessionsByWeek;
            }, []);
        })
    }
}
